import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Newspaper, DoorOpen, Plus, Mail, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { SECTION_MAP } from "@/lib/sections";

// Le Salon · Mobile bottom nav
// Salon · Rooms · [+] · Letters · Private Notes
// Profile is reachable via the top-right avatar dropdown (same items as desktop).
// Editor tools (Live, Founder's Desk, Editor's Letter) also live in the top bar / dropdown.
const readLastRoom = () => {
  try {
    const raw = localStorage.getItem("salon.compose.recent_rooms");
    const arr = raw ? JSON.parse(raw) : [];
    const first = Array.isArray(arr) ? arr[0] : null;
    return first && SECTION_MAP[first] ? first : null;
  } catch { return null; }
};

const TABS = [
  { id: "salon", label: "Salon", icon: Newspaper, path: "/feed", match: (p) => p === "/feed" },
  { id: "rooms", label: "Rooms", icon: DoorOpen, path: "/rooms", match: (p) => p === "/rooms" || p.startsWith("/room/") },
  { id: "post", label: "Post", icon: Plus, path: null, match: () => false, emphasis: true },
  { id: "letters", label: "Letters", icon: Mail, path: "/journal", match: (p) => p === "/journal" || p.startsWith("/journal/") },
  { id: "notes", label: "Notes", icon: MessageCircle, path: "/messages", match: (p) => p.startsWith("/messages") },
];

export const BottomTabBar = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unreadMessages, setUnreadMessages] = useState(0);

  useEffect(() => {
    if (!user) return;
    let active = true;
    const fetch = async () => {
      try {
        const { data } = await api.get("/conversations/unread-count");
        if (active) setUnreadMessages(data?.count || 0);
      } catch { /* silent */ }
    };
    fetch();
    const t = setInterval(fetch, 15000);
    return () => { active = false; clearInterval(t); };
  }, [user]);

  if (!user) return null;
  if (location.pathname === "/install" || location.pathname === "/") return null;

  return (
    <nav
      data-testid="bottom-tab-bar"
      className="flex md:hidden"
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        height: 68,
        background: "rgba(250, 249, 245, 0.96)",
        backdropFilter: "blur(20px)",
        borderTop: "1px solid var(--border-default)",
        alignItems: "stretch",
        zIndex: 220,
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {TABS.map((t) => {
        const active = t.match(location.pathname);
        const color = active ? "var(--brand-accent)" : "var(--text-primary)";
        const Icon = t.icon;
        const isNotes = t.id === "notes";
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => {
              if (t.id === "post") {
                const last = readLastRoom();
                navigate(last ? `/room/${last}` : "/rooms");
              } else {
                navigate(t.path);
              }
            }}
            data-testid={`tab-${t.id}`}
            aria-label={t.label}
            style={{
              flex: 1,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
              color: color,
              transition: "color 0.2s ease",
              touchAction: "manipulation",
              WebkitTapHighlightColor: "transparent",
              position: "relative",
            }}
          >
            <div
              style={
                t.emphasis
                  ? {
                      width: 38,
                      height: 38,
                      background: "var(--brand-accent)",
                      color: "var(--surface)",
                      display: "grid",
                      placeItems: "center",
                      marginTop: -10,
                      borderRadius: 4,
                      transition: "background 0.2s ease",
                    }
                  : { position: "relative" }
              }
            >
              <Icon
                size={t.emphasis ? 20 : 22}
                strokeWidth={1.5}
                color={t.emphasis ? "var(--surface)" : color}
              />
              {isNotes && unreadMessages > 0 && (
                <span
                  data-testid="tab-notes-unread"
                  style={{
                    position: "absolute",
                    top: -4, right: -8,
                    minWidth: 16, height: 16, padding: "0 4px",
                    borderRadius: 8,
                    background: "var(--brand-accent)",
                    color: "#FAF9F5",
                    fontSize: 9, fontWeight: 600,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    fontFamily: "'Outfit', sans-serif", letterSpacing: 0,
                  }}
                >
                  {unreadMessages > 9 ? "9+" : unreadMessages}
                </span>
              )}
            </div>
            <span
              style={{
                fontFamily: "'Outfit', sans-serif",
                fontSize: 9.5,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                fontWeight: active ? 600 : 400,
              }}
            >
              {t.label}
            </span>
          </button>
        );
      })}
    </nav>
  );
};
